import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { useNavigate } from "react-router-dom";
import QuickActionsSidebar from "../DistributorDashboard/QuickActionsSidebar";
import { getAdminActions } from "../../../config/actions";
import Pagination from "../../common/Pagination";
import { API_URL } from "../../../config";
import "../DistributorsAdd/DistributorsAdd.css";
import "../DistributorDashboard/DistributorDashboard.css";
import "../HelpContent/HelpContent.css";
import "./CapnoGoTickets.css";

const PAGE_SIZE = 10;

const STATUS_LABELS = {
  open: "Open",
  in_progress: "In Progress",
  closed: "Closed",
};

const EMPTY_FORM = {
  title: "",
  description: "",
  name: "",
  email: "",
  status: "open",
};

// Ticket and comment forms carry an optional attachment, so writes go as FormData.
const ticketRequest = async (path, { method = "GET", fields, file } = {}) => {
  let body;
  if (fields) {
    body = new FormData();
    Object.entries(fields).forEach(([key, value]) => body.append(key, value));
    if (file) body.append("file", file);
  }
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: { "x-access-token": localStorage.getItem("accessToken") },
    body,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.status === false) {
    throw new Error(data.message || "Something went wrong");
  }
  return data;
};

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "N/A" : date.toLocaleString();
};

const isImage = (fileName) => /\.(png|jpe?g|gif|webp)$/i.test(fileName || "");

const Attachment = ({ file, fileName }) => {
  if (!file) return null;
  return (
    <div className="ticket-attachment">
      {isImage(fileName) && (
        <a href={file} target="_blank" rel="noopener noreferrer">
          <img src={file} alt={fileName} />
        </a>
      )}
      <a href={file} target="_blank" rel="noopener noreferrer">
        <i className="fas fa-paperclip"></i> {fileName || "Attachment"}
      </a>
    </div>
  );
};

const CapnoGoTickets = () => {
  const navigate = useNavigate();
  const isAdmin = localStorage.getItem("user_type") === "7";
  const userId = localStorage.getItem("user_id");

  const [expandedMenu, setExpandedMenu] = useState(null);

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formFile, setFormFile] = useState(null);
  const [removeFile, setRemoveFile] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [commentTarget, setCommentTarget] = useState(null);
  const [commentTitle, setCommentTitle] = useState("");
  const [commentDescription, setCommentDescription] = useState("");
  const [commentFile, setCommentFile] = useState(null);
  const [commentSaving, setCommentSaving] = useState(false);
  const [commentError, setCommentError] = useState("");

  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewTicket, setViewTicket] = useState(null);
  const [viewComments, setViewComments] = useState([]);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [statusTarget, setStatusTarget] = useState(null);
  const [statusValue, setStatusValue] = useState("open");
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState("");

  const [successMessage, setSuccessMessage] = useState("");

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const data = await ticketRequest("/capnogo-tickets");
      setTickets(data.tickets || []);
    } catch (err) {
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchTickets();
    } else {
      navigate("/");
    }
  }, []);

  if (!isAdmin) return null;

  const actions = getAdminActions(userId);

  const totalPages = Math.max(1, Math.ceil(tickets.length / PAGE_SIZE));
  const paginatedTickets = tickets.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const openForm = (ticket = null) => {
    setEditing(ticket);
    setForm(
      ticket
        ? {
            title: ticket.title,
            description: ticket.description,
            name: ticket.name,
            email: ticket.email,
            status: ticket.status,
          }
        : EMPTY_FORM
    );
    setFormFile(null);
    setRemoveFile(false);
    setError("");
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
    setEditing(null);
  };

  const setField = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    setError("");
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (
      !form.title.trim() ||
      !form.description.trim() ||
      !form.name.trim() ||
      !form.email.trim()
    ) {
      setError("Title, description, name and email are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const fields = {
        title: form.title,
        description: form.description,
        name: form.name,
        email: form.email,
      };
      if (editing) {
        fields.status = form.status;
        if (removeFile && !formFile) fields.remove_file = "1";
      }
      await ticketRequest(
        editing ? `/capnogo-tickets/${editing.id}` : "/capnogo-tickets",
        { method: editing ? "PUT" : "POST", fields, file: formFile }
      );
      setFormOpen(false);
      setSuccessMessage(
        editing ? "Ticket updated successfully." : "Ticket created successfully."
      );
      setEditing(null);
      await fetchTickets();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const openComment = (ticket) => {
    setCommentTarget(ticket);
    setCommentTitle("");
    setCommentDescription("");
    setCommentFile(null);
    setCommentError("");
  };

  const closeComment = () => {
    if (commentSaving) return;
    setCommentTarget(null);
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentTitle.trim() || !commentDescription.trim()) {
      setCommentError("Title and description are required.");
      return;
    }
    setCommentSaving(true);
    setCommentError("");
    try {
      await ticketRequest(`/capnogo-tickets/${commentTarget.id}/comments`, {
        method: "POST",
        fields: { title: commentTitle, description: commentDescription },
        file: commentFile,
      });
      setCommentTarget(null);
      setSuccessMessage("Comment added successfully.");
      await fetchTickets();
    } catch (err) {
      setCommentError(err.message);
    } finally {
      setCommentSaving(false);
    }
  };

  const openView = async (ticket) => {
    setViewTicket(ticket);
    setViewComments([]);
    setViewOpen(true);
    setViewLoading(true);
    try {
      const data = await ticketRequest(`/capnogo-tickets/${ticket.id}`);
      setViewTicket(data.ticket);
      setViewComments(data.comments || []);
    } catch (err) {
      setViewComments([]);
    } finally {
      setViewLoading(false);
    }
  };

  const openStatus = (ticket) => {
    setStatusTarget(ticket);
    setStatusValue(ticket.status);
    setStatusError("");
  };

  const closeStatus = () => {
    if (statusSaving) return;
    setStatusTarget(null);
  };

  const handleStatusSave = async (e) => {
    e.preventDefault();
    if (statusValue === statusTarget.status) {
      setStatusTarget(null);
      return;
    }
    setStatusSaving(true);
    setStatusError("");
    try {
      await ticketRequest(`/capnogo-tickets/${statusTarget.id}/status`, {
        method: "PATCH",
        fields: { status: statusValue },
      });
      setTickets((prev) =>
        prev.map((t) =>
          t.id === statusTarget.id ? { ...t, status: statusValue } : t
        )
      );
      setStatusTarget(null);
      setSuccessMessage(
        `Ticket status updated to ${STATUS_LABELS[statusValue]}.`
      );
    } catch (err) {
      setStatusError(err.message);
    } finally {
      setStatusSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await ticketRequest(`/capnogo-tickets/${deleteTarget.id}`, {
        method: "DELETE",
      });
      setDeleteTarget(null);
      const remaining = tickets.length - 1;
      setCurrentPage((p) =>
        Math.min(p, Math.max(1, Math.ceil(remaining / PAGE_SIZE)))
      );
      setSuccessMessage("Ticket deleted successfully.");
      await fetchTickets();
    } catch (err) {
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  // The ticket itself is the first entry; comments follow in the order they were added.
  const timeline = viewTicket
    ? [{ ...viewTicket, entryType: "ticket" }].concat(
        viewComments.map((comment) => ({ ...comment, entryType: "comment" }))
      )
    : [];

  return (
    <div className="distributors-add-container">
      <div className="distributors-add-dashboard">
        <QuickActionsSidebar
          actions={actions}
          expandedMenu={expandedMenu}
          setExpandedMenu={setExpandedMenu}
        />
        <div className="distributors-add-content">
          <div className="add-btn-wrp add-btn-wrp2">
            <div className="head-listing">
              <h3>CapnoGO Tickets</h3>
            </div>
            <div className="add-btn ticket-header-actions">
              <button
                className="ticket-back-btn"
                onClick={() => navigate("/admin/capnogo-users")}
              >
                Back
              </button>
              <button onClick={() => openForm()}>Create Ticket</button>
            </div>
          </div>

          <div className="distributors-table-section">
            {loading ? (
              <div className="loading-state">
                <div className="loader-spinner"></div>
                <p>Loading tickets...</p>
              </div>
            ) : tickets.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-ticket-alt"></i>
                <p>No tickets yet</p>
              </div>
            ) : (
              <>
                <div className="table-wrapper">
                  <table className="distributors-table help-table">
                    <thead>
                      <tr>
                        {/* <th>ID</th> */}
                        <th>Title</th>
                        <th>Name</th>
                        <th>Email</th>
                        {/* <th>User ID</th> */}
                        <th>Status</th>
                        {/* <th>Created</th> */}
                        <th className="ticket-col-actions">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedTickets.map((ticket) => (
                        <tr key={ticket.id}>
                          {/* <td>{ticket.id}</td> */}
                          <td>{ticket.title}</td>
                          <td>{ticket.name}</td>
                          <td>{ticket.email}</td>
                          {/* <td>{ticket.user_id || "N/A"}</td> */}
                          <td>
                            <span
                              className={`ticket-status ticket-status-${ticket.status}`}
                            >
                              {STATUS_LABELS[ticket.status] || ticket.status}
                            </span>
                          </td>
                          {/* <td>{formatDate(ticket.created)}</td> */}
                          <td>
                            <div className="help-actions-cell">
                              <button
                                className="action-btn view-btn"
                                title="View"
                                onClick={() => openView(ticket)}
                              >
                                <i className="fa fa-eye"></i>
                              </button>
                              <button
                                className="action-btn"
                                title="Edit"
                                onClick={() => openForm(ticket)}
                              >
                                <i className="fa fa-pencil-square-o"></i>
                              </button>
                              <button
                                className="action-btn"
                                title="Add Comment"
                                onClick={() => openComment(ticket)}
                              >
                                <i className="fa fa-comment"></i>
                              </button>
                              <button
                                className="action-btn"
                                title="Update Status"
                                onClick={() => openStatus(ticket)}
                              >
                                <i className="fa fa-exchange"></i>
                              </button>
                              <button
                                className="action-btn cancel-btn"
                                title="Delete"
                                onClick={() => setDeleteTarget(ticket)}
                              >
                                <i className="fa fa-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Create / Edit Ticket Modal */}
      <Modal isOpen={formOpen} toggle={closeForm} centered size="lg">
        <ModalHeader className="modal-header-custom" toggle={closeForm}>
          {editing ? `Edit Ticket #${editing.id}` : "Create Ticket"}
        </ModalHeader>
        <form autoComplete="off" onSubmit={handleSave} style={{paddingBottom: "0"}}>
          <ModalBody className="modal-body-custom">
            <div className="help-form-group">
              <label>Title*</label>
              <input type="text" value={form.title} onChange={setField("title")} />
            </div>
            <div className="help-form-group">
              <label>Description*</label>
              <textarea
                className="ticket-textarea"
                rows={4}
                value={form.description}
                onChange={setField("description")}
              />
            </div>
            <div className="ticket-form-row">
              <div className="help-form-group">
                <label>Name*</label>
                <input type="text" value={form.name} onChange={setField("name")} />
              </div>
              <div className="help-form-group">
                <label>Email*</label>
                <input type="text" value={form.email} onChange={setField("email")} />
              </div>
            </div>
            {editing && (
              <div className="help-form-group">
                <label>Status</label>
                <select value={form.status} onChange={setField("status")}>
                  {Object.entries(STATUS_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="help-form-group">
              <label>File / Image</label>
              {editing && editing.file && !removeFile && !formFile && (
                <div className="ticket-current-file">
                  <a href={editing.file} target="_blank" rel="noopener noreferrer">
                    <i className="fas fa-paperclip"></i>{" "}
                    {editing.file_name || "Attachment"}
                  </a>
                  <button type="button" onClick={() => setRemoveFile(true)}>
                    Remove
                  </button>
                </div>
              )}
              <input
                type="file"
                onChange={(e) => setFormFile(e.target.files[0] || null)}
              />
              <div className="help-hint">
                Images, PDF, Office documents, CSV, TXT or ZIP. Maximum 10 MB.
              </div>
            </div>
            {/* <div className="help-hint">
              The user ID is filled in automatically when the email matches a
              CapnoGO user.
            </div> */}
            {error && <div className="help-error">{error}</div>}
          </ModalBody>
          <ModalFooter className="modal-footer-custom help-modal-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={closeForm}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary-modern" disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Add Comment Modal */}
      <Modal isOpen={!!commentTarget} toggle={closeComment} centered size="lg">
        <ModalHeader className="modal-header-custom" toggle={closeComment}>
          Add Comment to Ticket #{commentTarget?.id}
        </ModalHeader>
        <form autoComplete="off" onSubmit={handleAddComment} style={{paddingBottom: "0"}}>
          <ModalBody className="modal-body-custom">
            <div className="help-form-group">
              <label>Title*</label>
              <input
                type="text"
                value={commentTitle}
                onChange={(e) => {
                  setCommentTitle(e.target.value);
                  setCommentError("");
                }}
              />
            </div>
            <div className="help-form-group">
              <label>Description*</label>
              <textarea
                className="ticket-textarea"
                rows={4}
                value={commentDescription}
                onChange={(e) => {
                  setCommentDescription(e.target.value);
                  setCommentError("");
                }}
              />
            </div>
            <div className="help-form-group">
              <label>File / Image</label>
              <input
                type="file"
                onChange={(e) => setCommentFile(e.target.files[0] || null)}
              />
              <div className="help-hint">
                Images, PDF, Office documents, CSV, TXT or ZIP. Maximum 10 MB.
              </div>
            </div>
            {commentError && <div className="help-error">{commentError}</div>}
          </ModalBody>
          <ModalFooter className="modal-footer-custom help-modal-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={closeComment}
              disabled={commentSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary-modern"
              disabled={commentSaving}
            >
              {commentSaving ? "Saving..." : "Add Comment"}
            </button>
          </ModalFooter>
        </form>
      </Modal>

      {/* View Ticket Modal */}
      <Modal isOpen={viewOpen} toggle={() => setViewOpen(false)} centered size="lg">
        <ModalHeader
          className="modal-header-custom"
          toggle={() => setViewOpen(false)}
        >
          Ticket #{viewTicket?.id}
        </ModalHeader>
        <ModalBody className="modal-body-custom">
          {viewTicket && (
            <>
              <div className="ticket-summary">
                <div>
                  <span className="label">Name:</span> {viewTicket.name}
                </div>
                <div>
                  <span className="label">Email:</span> {viewTicket.email}
                </div>
                {/* <div>
                  <span className="label">User ID:</span>{" "}
                  {viewTicket.user_id || "N/A"}
                </div> */}
                <div>
                  <span className="label">Status:</span>{" "}
                  <span
                    className={`ticket-status ticket-status-${viewTicket.status}`}
                  >
                    {STATUS_LABELS[viewTicket.status] || viewTicket.status}
                  </span>
                </div>
              </div>

              <div className="ticket-timeline">
                {timeline.map((entry) => (
                  <div
                    className="ticket-timeline-item"
                    key={`${entry.entryType}-${entry.id}`}
                  >
                    <div className="ticket-timeline-dot"></div>
                    <div className="ticket-timeline-content">
                      <div className="ticket-timeline-meta">
                        {entry.entryType === "ticket"
                          ? "Ticket created"
                          : "Comment"}{" "}
                        · {formatDate(entry.created)}
                      </div>
                      <div className="ticket-timeline-title">{entry.title}</div>
                      <div className="ticket-timeline-description">
                        {entry.description}
                      </div>
                      <Attachment file={entry.file} fileName={entry.file_name} />
                    </div>
                  </div>
                ))}
              </div>
              {viewLoading && <div className="help-hint">Loading comments...</div>}
            </>
          )}
        </ModalBody>
        <ModalFooter className="modal-footer-custom help-modal-footer">
          <button className="btn-secondary" onClick={() => setViewOpen(false)}>
            Close
          </button>
        </ModalFooter>
      </Modal>

      {/* Delete Ticket Modal */}
      <Modal isOpen={!!deleteTarget} toggle={() => setDeleteTarget(null)} centered>
        <ModalHeader
          className="modal-header-custom"
          toggle={() => setDeleteTarget(null)}
        >
          Delete Ticket
        </ModalHeader>
        <ModalBody className="modal-body-custom">
          Delete "{deleteTarget?.title}" and its comments? This cannot be undone.
        </ModalBody>
        <ModalFooter className="modal-footer-custom help-modal-footer">
          <button
            className="btn-secondary"
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
          >
            Cancel
          </button>
          <button
            className="btn-primary-modern"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete"}
          </button>
        </ModalFooter>
      </Modal>

      {/* Update Status Modal */}
      <Modal isOpen={!!statusTarget} toggle={closeStatus} centered>
        <ModalHeader className="modal-header-custom" toggle={closeStatus}>
          Update Status
        </ModalHeader>
        <form onSubmit={handleStatusSave} style={{paddingBottom: "0"}}>
          <ModalBody className="modal-body-custom">
            <div className="help-form-group">
              <label>Status for "{statusTarget?.title}"</label>
              <select
                value={statusValue}
                onChange={(e) => {
                  setStatusValue(e.target.value);
                  setStatusError("");
                }}
              >
                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            {statusError && <div className="help-error">{statusError}</div>}
          </ModalBody>
          <ModalFooter className="modal-footer-custom help-modal-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={closeStatus}
              disabled={statusSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary-modern"
              disabled={statusSaving}
            >
              {statusSaving ? "Saving..." : "Update"}
            </button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Success Modal */}
      <Modal
        isOpen={!!successMessage}
        toggle={() => setSuccessMessage("")}
        centered
      >
        <ModalHeader
          className="modal-header-custom"
          toggle={() => setSuccessMessage("")}
        >
          Success
        </ModalHeader>
        <ModalBody className="modal-body-custom">
          <div className="success-state ticket-success-state">
            <div className="success-icon ticket-success-icon">
              <i className="fas fa-check-circle"></i>
            </div>
            <h3>Success!</h3>
            <p>{successMessage}</p>
          </div>
        </ModalBody>
        <ModalFooter className="modal-footer-custom help-modal-footer">
          <button
            className="btn-primary-modern"
            onClick={() => setSuccessMessage("")}
          >
            OK
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default CapnoGoTickets;
